import { CostColumn } from './cost-column';
import type { CostRow } from './cost-row';

/**
 * O recorte da tabela: o que aparece e em que ordem.
 *
 * Vive inteiro na query da URL, então o link que você manda para alguém abre a
 * mesma tabela que você está olhando — filtro é estado da mesma tela, não outra
 * tela, e por isso não vira caminho.
 */
export class CostsFilter {
  private static readonly ORIGINS: Record<string, string> = {
    atendimento: 'Atendimento',
    rotina: 'Rotina nossa',
  };

  private constructor(
    readonly search: string,
    readonly origin: string,
    readonly onlyUnpriced: boolean,
    readonly withTests: boolean,
    readonly column: CostColumn,
    readonly descending: boolean,
  ) {}

  static fromQuery(query: (key: string) => string | null): CostsFilter {
    return new CostsFilter(
      query('busca') ?? '',
      CostsFilter.ORIGINS[query('origem') ?? ''] ? (query('origem') as string) : '',
      query('sem-preco') === '1',
      query('com-testes') === '1',
      CostColumn.fromSlug(query('ordem')),
      query('dir') !== 'asc',
    );
  }

  static originOptions(): { slug: string; label: string }[] {
    return Object.entries(CostsFilter.ORIGINS).map(([slug, label]) => ({ slug, label }));
  }

  /** Sem teste é o padrão: a tela abre no que a operação custou. Ligar os testes
   *  é que é um recorte, e por isso conta como filtro aceso. */
  get isEmpty(): boolean {
    return !this.search && !this.origin && !this.onlyUnpriced && !this.withTests;
  }

  /** O patch que zera o recorte sem tocar em período nem aba. */
  static cleared(): Record<string, string | null> {
    return { busca: null, origem: null, 'sem-preco': null, 'com-testes': null };
  }

  /** O patch que liga e desliga "com os meus testes". */
  get withTestsPatch(): Record<string, string | null> {
    return { 'com-testes': this.withTests ? null : '1' };
  }

  /** Clicar na coluna já ordenada inverte; clicar em outra começa pela maior. */
  sortPatch(column: CostColumn): Record<string, string | null> {
    if (!this.column.is(column)) return { ordem: column.slug, dir: null };
    return { ordem: column.slug, dir: this.descending ? 'asc' : null };
  }

  directionOf(column: CostColumn): 'ascending' | 'descending' | 'none' {
    if (!this.column.is(column)) return 'none';
    return this.descending ? 'descending' : 'ascending';
  }

  /**
   * Os dois filtros fazem coisas diferentes de propósito.
   *
   * **Busca** é "ache isto": a linha do cliente fica inteira se ela ou um workflow
   * dela casa — esconder os irmãos esconderia a comparação que se foi fazer.
   *
   * **Origem** e **sem preço** são "mostre só esta fatia": aí os filhos são
   * recortados e o total do cliente é REFEITO com o que sobrou, senão a linha
   * anuncia um valor que não é a soma do que está embaixo dela.
   */
  apply(rows: CostRow[]): CostRow[] {
    const sliced = rows.map((row) => this.slice(row)).filter((row): row is CostRow => row !== null);
    const found = sliced.filter((row) => this.found(row));
    const order = this.descending ? -1 : 1;
    return [...found].sort((left, right) => this.column.compare(left, right) * order);
  }

  private slice(row: CostRow): CostRow | null {
    if (!this.slices) return row;
    if (!row.hasChildren) return this.inSlice(row) ? row : null;

    const kept = row.children.filter((child) => this.inSlice(child));
    if (kept.length === 0) return null;
    return row.rebuiltFrom(kept);
  }

  private get slices(): boolean {
    return Boolean(this.origin) || this.onlyUnpriced || !this.withTests;
  }

  private inSlice(row: CostRow): boolean {
    if (!this.withTests && row.isOurTest) return false;
    if (this.onlyUnpriced && row.isPriced) return false;
    if (this.origin === 'atendimento' && row.isInternalRoutine) return false;
    if (this.origin === 'rotina' && !row.isInternalRoutine) return false;
    return true;
  }

  private found(row: CostRow): boolean {
    if (!this.search) return true;
    return this.named(row) || row.children.some((child) => this.named(child));
  }

  private named(row: CostRow): boolean {
    const alvo = `${row.name} ${row.subtitle ?? ''}`.toLowerCase();
    return alvo.includes(this.search.toLowerCase().trim());
  }
}
